import { memo, useMemo } from "react";
import {
  haversineDistanceM,
  formatDistance,
  walkDurationText,
  carDurationText,
} from "../lib/distance";
import { imageUrl } from "../lib/dataClient";

/**
 * 카페 카드 리스트 (시트 안의 초기 화면)
 *
 * 카드 디자인: 텍스트 위주 + 사장님 추천 음식점 이름 미리보기
 *   - chip(Cafe) + 이름 + 동네/카테고리
 *   - 📍 내 위치 거리
 *   - ⭐ 사장님이 추천한 음식점 이름 1-2개 미리보기
 */
export default memo(function CafeList({
  cafes,
  places,
  region,
  userLocation,
  userRegionName,
  onSelectCafe,
}) {
  const locationLabel = useMemo(() => {
    if (!region || region === "all") return "제주도 전체";
    const regionLabels = {
      jeju_city: "제주시",
      aewol: "애월",
      hallim: "한림",
      jocheon: "조천",
      gujwa: "구좌",
      chuja: "추자",
      udo: "우도",
      seogwipo_city: "서귀포시",
      daejeong: "대정",
      andeok: "안덕",
      namwon: "남원",
      seongsan: "성산",
      pyoseon: "표선",
    };
    return regionLabels[region] ?? "제주도";
  }, [region]);

  // 카페별 메타 + 내 위치 거리순 정렬
  const cafesWithMeta = useMemo(() => {
    const list = cafes.map((cafe) => {
      const recommends = (cafe.recommends || [])
        .map((r) => places.find((pp) => pp.id === r.placeId))
        .filter((p) => p && p.lat != null);
      const recommendCount = recommends.length;
      const recommendNames = recommends.map((p) => p.name);
      const distFromUser =
        userLocation && cafe.lat != null
          ? haversineDistanceM(
              userLocation.lat,
              userLocation.lng,
              cafe.lat,
              cafe.lng
            )
          : null;
      return { ...cafe, recommendCount, recommendNames, distFromUser };
    });
    if (userLocation) {
      list.sort(
        (a, b) => (a.distFromUser ?? Infinity) - (b.distFromUser ?? Infinity)
      );
    }
    return list;
  }, [cafes, places, userLocation]);

  return (
    <div className="cf-cafelist">
      <div className="cf-cafelist__header">
        <span className="cf-cafelist__pin">📍</span>
        <div className="cf-cafelist__header-text">
          <span className="cf-cafelist__location">
            {userLocation
              ? userRegionName
                ? `${userRegionName} 근처`
                : "내 현위치"
              : locationLabel}
          </span>
          <span className="cf-cafelist__meta">
            {userLocation
              ? `${locationLabel} · 카페 ${cafes.length}곳 · 가까운 순`
              : `카페 ${cafes.length}곳`}
          </span>
        </div>
      </div>

      <div className="cf-cafelist__items">
        {cafesWithMeta.map((cafe) => {
          const category = cafe.naverCategory?.split(">").pop()?.trim();

          // 추천 미리보기 텍스트 (최대 2개 이름 + "외 N곳")
          let recPreview = null;
          if (cafe.recommendCount > 0) {
            const preview = cafe.recommendNames.slice(0, 2).join(", ");
            const extra = cafe.recommendCount - 2;
            recPreview = extra > 0 ? `${preview} 외 ${extra}곳` : preview;
          }

          return (
            <button
              key={cafe.id}
              className="cf-cafecard"
              onClick={() => onSelectCafe?.(cafe)}
            >
              <div className="cf-cafecard__top">
                <div className="cf-cafecard__head">
                  <div className="cf-cafecard__chip">
                    <span className="cf-cafecard__chip-dot" />
                    Cafe
                  </div>
                  <div className="cf-cafecard__name">{cafe.name}</div>
                  {(cafe.neighborhood || category) && (
                    <div className="cf-cafecard__sub">
                      {cafe.neighborhood && <span>{cafe.neighborhood}</span>}
                      {cafe.neighborhood && category && (
                        <span className="cf-cafecard__sub-dot">·</span>
                      )}
                      {category && <span>{category}</span>}
                    </div>
                  )}
                </div>

                {cafe.thumbnail && (
                  <div className="cf-cafecard__hero">
                    <img
                      src={imageUrl(cafe.thumbnail)}
                      alt={cafe.name}
                      className="cf-cafecard__hero-img"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              {cafe.distFromUser != null && (
                <div className="cf-cafecard__dist">
                  <span className="cf-cafecard__dist-icon">📍</span>
                  내 위치에서{" "}
                  <strong>{formatDistance(cafe.distFromUser)}</strong>
                  <span className="cf-cafecard__dist-sub">
                    {" · "}🚶 {walkDurationText(cafe.distFromUser)} · 🚗{" "}
                    {carDurationText(cafe.distFromUser)}
                  </span>
                </div>
              )}

              {recPreview && (
                <div className="cf-cafecard__rec">
                  <span className="cf-cafecard__rec-icon">⭐</span>
                  <span className="cf-cafecard__rec-label">
                    사장님 추천 {cafe.recommendCount}곳
                  </span>
                  <span className="cf-cafecard__rec-dot">·</span>
                  <span className="cf-cafecard__rec-names">{recPreview}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});
